import { statusFilterToActive } from 'src/app/shared/utilities/status-filter';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { CompanyService } from '../../companies/services/company.service';
import { ICompany } from '../../companies/models/company.interface';
import { ICadastroCentroCusto, ICentroCusto, IListByFilterCentroCusto } from '../models/centrocusto';
import { CentroCustosService } from '../services/centro-custos.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-centro-custos',
  templateUrl: './centro-custos.component.html',
  styleUrls: ['./centro-custos.component.scss']
})
export class CentroCustosComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  private readonly destroy$ = new Subject<void>();
  private centrosSub?: Subscription;
  acaoInput = new UntypedFormControl();
  statusControl = new UntypedFormControl('all');
  ordenarControl = new UntypedFormControl(1);
  centros: ICadastroCentroCusto[] = [];
  opcoesOrdenacao = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    },
    {
      value: EOrdenacao.MaisNovos,
      label: 'Mais novos'
    },
    {
      value: EOrdenacao.MaisAntigos,
      label: 'Mais antigos'
    }
  ];

  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  companyId?: string;

  /** Filtro por empresa para quem enxerga mais de uma (interno Oliv-e ou parceiro). */
  companyControl = new UntypedFormControl(null);
  companies: ICompany[] = [];

  constructor(
    private readonly centroCustoservice: CentroCustosService,
    private readonly sessionService: SessionService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyId = this.sessionService.getSession().companyId;

    this.carregarCentroCusto();

    if (!this.companyId) {
      this.companyService
        .listarEmpresasAtivas()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (resposta) => (this.companies = resposta.data ?? []),
          error: (erro) => {
            this.companies = [];
            if (!interceptorAvisouErro(erro)) {
              this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
            }
          }
        });
    }

    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });

    this.ordenarControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((ordenacaoSelecionada: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.recarregarDoInicio();
    });

    this.statusControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filtrarCentroCusto(): void {
    this.recarregarDoInicio();
  }

  limparFiltros(): void {
    this.acaoInput.reset();
    this.statusControl.reset('all', { emitEvent: false });
    this.companyControl.reset(null, { emitEvent: false });
    this.recarregarDoInicio();
  }

  carregarCentroCusto(): void {
    const filtros = this.buildCentroCustoFilterOptions();

    // Cancela a busca anterior: uma resposta atrasada de outro filtro não sobrescreve a atual.
    this.centrosSub?.unsubscribe();
    this.centrosSub = this.centroCustoservice
      .listarTodosCentrosCusto(filtros)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.centros = res.data;
          this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
          this.dadosPaginacao.itensTotal = res.count!;
        },
        // Falhou: não deixa na tela a lista da busca anterior como se fosse o resultado dos filtros atuais.
        error: (erro) => {
          this.centros = [];
          this.dadosPaginacao.totalPaginas = 0;
          this.dadosPaginacao.itensTotal = 0;
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Erro ao carregar os centros de custo. Tente novamente.', EMessageType.Error);
          }
        }
      });
  }

  /** Mudou um filtro, a ordenação ou o tamanho da página: a página atual pode não existir mais. */
  private recarregarDoInicio(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.carregarCentroCusto();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;

    this.carregarCentroCusto();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;

    this.recarregarDoInicio();
  }
  public downloadExcel(): void {
    const filtros = this.buildCentroCustoFilterOptions();

    this.centroCustoservice.exportarExcelCentrosCustos(filtros);
  }

  buildCentroCustoFilterOptions(): IListByFilterCentroCusto {
    const filtros: IListByFilterCentroCusto = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    filtros.active = statusFilterToActive(this.statusControl.value);

    if (this.acaoInput.value) {
      filtros.searchText = String(this.acaoInput.value);
    }

    const partnerId = this.sessionService.getSession().partnerId;
    // A empresa da sessão prevalece; o filtro só vale para quem não está preso a uma empresa.
    const companyId = this.sessionService.getSession().companyId || this.companyControl.value;
    const accountRole = this.sessionService.getSession().accountRole;

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    return filtros;
  }
}
